import * as React from "react";
import { cn } from "@/shadcn/lib/utils";

import { Button } from "@/shadcn/components/ui/button";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from "lucide-react";

// playerdex · registry v1 · shadcn@4.21.0

const Pagination = React.forwardRef(function Pagination(
  { className, ...props },
  ref
) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn("mx-auto flex w-full justify-center", className)}
      ref={ref}
      {...props}
    />
  );
});

const PaginationContent = React.forwardRef(function PaginationContent(
  { className, ...props },
  ref
) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn("gap-1 flex items-center", className)}
      ref={ref}
      {...props}
    />
  );
});

const PaginationItem = React.forwardRef(function PaginationItem(
  { ...props },
  ref
) {
  return <li data-slot="pagination-item" ref={ref} {...props} />;
});

const PaginationLink = React.forwardRef(function PaginationLink(
  { className, isActive, size = "icon", ...props },
  ref
) {
  return (
    <Button
      variant={isActive ? "outline" : "ghost"}
      size={size}
      className={cn(className)}
      nativeButton={false}
      render={
        <a
          aria-current={isActive ? "page" : undefined}
          data-slot="pagination-link"
          data-active={isActive}
          ref={ref}
          {...props}
        />
      }
    />
  );
});

const PaginationPrevious = React.forwardRef(function PaginationPrevious(
  { className, text = "Previous", ...props },
  ref
) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={cn("pl-2!", className)}
      ref={ref}
      {...props}
    >
      <ChevronLeftIcon data-icon="inline-start" />
      <span className="hidden sm:block">{text}</span>
    </PaginationLink>
  );
});

const PaginationNext = React.forwardRef(function PaginationNext(
  { className, text = "Next", ...props },
  ref
) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={cn("pr-2!", className)}
      ref={ref}
      {...props}
    >
      <span className="hidden sm:block">{text}</span>
      <ChevronRightIcon data-icon="inline-end" />
    </PaginationLink>
  );
});

const PaginationEllipsis = React.forwardRef(function PaginationEllipsis(
  { className, ...props },
  ref
) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn(
        "size-9 [&_svg:not([class*='size-'])]:size-4 flex items-center justify-center",
        className
      )}
      ref={ref}
      {...props}
    >
      <MoreHorizontalIcon />
      <span className="sr-only">More pages</span>
    </span>
  );
});

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
